import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { LuPencil, LuTrash2, LuPlus, LuCheck, LuX } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";

export function FailTypesSettings() {
  const [categories, setCategories] = useState([]);
  const [allFailTypes, setAllFailTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  // Inline add state
  const [addingFor, setAddingFor] = useState(null); // category id
  const [addingName, setAddingName] = useState("");
  const [addingSaving, setAddingSaving] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingSaving, setEditingSaving] = useState(false);

  const addInputRef = useRef(null);
  const editInputRef = useRef(null);

  const loadAll = async () => {
    try {
      const [catRes, failRes] = await Promise.all([
        axios.get(`${API}/asset-categories/`),
        axios.get(`${API}/asset-fail-types/`),
      ]);
      setCategories(catRes.data);
      setAllFailTypes(failRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (addingFor !== null && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [addingFor]);

  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const toggleCollapse = (catId) =>
    setCollapsed((p) => ({ ...p, [catId]: !p[catId] }));

  const startAdding = (categoryId) => {
    setEditingId(null);
    setAddingFor(categoryId);
    setAddingName("");
  };

  const handleInlineAdd = async (categoryId) => {
    if (!addingName.trim()) return;
    setAddingSaving(true);
    try {
      const res = await axios.post(`${API}/asset-fail-types/`, {
        category: categoryId,
        name: addingName.trim(),
        order: 0,
      });
      setAllFailTypes((prev) => [...prev, res.data]);
      setAddingName("");
      addInputRef.current?.focus();
    } catch {}
    setAddingSaving(false);
  };

  const cancelAdding = () => {
    setAddingFor(null);
    setAddingName("");
  };

  const startEditing = (ft) => {
    setAddingFor(null);
    setEditingId(ft.id);
    setEditingName(ft.name);
  };

  const handleInlineEdit = async (ftId) => {
    if (!editingName.trim()) { setEditingId(null); return; }
    setEditingSaving(true);
    try {
      const res = await axios.patch(`${API}/asset-fail-types/${ftId}/`, {
        name: editingName.trim(),
      });
      setAllFailTypes((prev) => prev.map((f) => f.id === ftId ? res.data : f));
      setEditingId(null);
    } catch {}
    setEditingSaving(false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleDelete = async (ft) => {
    try {
      await axios.patch(`${API}/asset-fail-types/${ft.id}/`, { is_active: false });
      setAllFailTypes((prev) => prev.filter((f) => f.id !== ft.id));
    } catch {}
  };

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Fail Types</div>
        <div style={{ fontSize: "13px", color: colors.slate }}>
          Define pre-set fail reasons per EFSM — applies to all asset types within each category
        </div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            categories.map((cat) => {
              const failTypes = allFailTypes.filter((f) => f.category === cat.id);
              const isCollapsed = collapsed[cat.id];
              const isAddingHere = addingFor === cat.id;
              return (
                <div key={cat.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  {/* Category header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 20px",
                      backgroundColor: colors.darkGrey,
                      cursor: "pointer",
                    }}
                    onClick={() => toggleCollapse(cat.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ color: colors.white, fontWeight: "600", fontSize: "13px" }}>
                        {cat.name}
                      </span>
                      {failTypes.length > 0 && (
                        <span style={{
                          fontSize: "11px",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          color: "rgba(255,255,255,0.85)",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          fontWeight: "600",
                        }}>
                          {failTypes.length} fail type{failTypes.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); startAdding(cat.id); }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor: "transparent",
                          color: "rgba(255,255,255,0.8)",
                          border: "1.5px solid rgba(255,255,255,0.3)",
                          padding: "3px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          minHeight: "auto",
                        }}
                      >
                        <LuPlus size={12} />
                        Add
                      </button>
                      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>
                        {isCollapsed ? "▶" : "▼"}
                      </span>
                    </div>
                  </div>

                  {/* Fail types list — shown when not collapsed */}
                  {!isCollapsed && (
                    <div style={{ backgroundColor: colors.white }}>
                      {failTypes.map((ft) => (
                        <div
                          key={ft.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 20px 8px 32px",
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          {editingId === ft.id ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}>
                              <input
                                ref={editInputRef}
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleInlineEdit(ft.id);
                                  if (e.key === "Escape") cancelEditing();
                                }}
                                onBlur={() => handleInlineEdit(ft.id)}
                                disabled={editingSaving}
                                style={{
                                  ...inputStyle,
                                  padding: "4px 8px",
                                  fontSize: "13px",
                                  flex: 1,
                                  maxWidth: "320px",
                                }}
                              />
                              <button
                                onMouseDown={(e) => { e.preventDefault(); handleInlineEdit(ft.id); }}
                                disabled={editingSaving}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#16a34a", padding: "4px", minHeight: "auto",
                                  display: "flex", alignItems: "center",
                                }}
                              >
                                <LuCheck size={14} />
                              </button>
                              <button
                                onMouseDown={(e) => { e.preventDefault(); cancelEditing(); }}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#94a3b8", padding: "4px", minHeight: "auto",
                                  display: "flex", alignItems: "center",
                                }}
                              >
                                <LuX size={14} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ color: colors.red, fontSize: "9px" }}>●</span>
                                <span style={{ fontSize: "13px", color: colors.text }}>{ft.name}</span>
                              </div>
                              <div style={{ display: "flex", gap: "2px" }}>
                                <IconBtn onClick={() => startEditing(ft)} Icon={LuPencil} hoverColor="#2563eb" />
                                <IconBtn onClick={() => handleDelete(ft)} Icon={LuTrash2} hoverColor="#dc2626" />
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {/* Inline add row */}
                      {isAddingHere ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 20px 8px 32px",
                            backgroundColor: "#fafffe",
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          <span style={{ color: colors.red, fontSize: "9px" }}>●</span>
                          <input
                            ref={addInputRef}
                            value={addingName}
                            onChange={(e) => setAddingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleInlineAdd(cat.id);
                              if (e.key === "Escape") cancelAdding();
                            }}
                            disabled={addingSaving}
                            placeholder="Type fail type name…"
                            style={{
                              ...inputStyle,
                              padding: "4px 8px",
                              fontSize: "13px",
                              flex: 1,
                              maxWidth: "320px",
                            }}
                          />
                          <button
                            onClick={() => handleInlineAdd(cat.id)}
                            disabled={addingSaving || !addingName.trim()}
                            style={{
                              backgroundColor: colors.red,
                              color: colors.white,
                              border: "none",
                              padding: "4px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              minHeight: "auto",
                              opacity: addingName.trim() ? 1 : 0.5,
                            }}
                          >
                            {addingSaving ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={cancelAdding}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "#94a3b8", padding: "4px", minHeight: "auto",
                              display: "flex", alignItems: "center",
                            }}
                          >
                            <LuX size={14} />
                          </button>
                        </div>
                      ) : (
                        failTypes.length === 0 && (
                          <div style={{
                            padding: "10px 32px",
                            fontSize: "12px",
                            color: "#94a3b8",
                            fontStyle: "italic",
                          }}>
                            No fail types yet — click Add to create one.
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {!loading && (
            <div style={styles.count}>
              {allFailTypes.length} fail type{allFailTypes.length !== 1 ? "s" : ""} across {categories.length} categor{categories.length !== 1 ? "ies" : "y"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, Icon, hoverColor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none",
        border: "none",
        padding: "4px",
        borderRadius: "4px",
        cursor: "pointer",
        color: hovered ? hoverColor : "#94a3b8",
        minHeight: "auto",
        display: "flex",
        alignItems: "center",
        transition: "color 0.12s",
      }}
    >
      <Icon size={14} />
    </button>
  );
}
